import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Breadcrumbs,
  CircularProgress,
  FormControl,
  IconButton,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowBack, Home } from "@mui/icons-material";
import FileList from "components/fileStorage/FileList";
import FileSearch from "components/fileStorage/FileSearch";
import PaginationControls from "components/fileStorage/PaginationControls";
import { useFilePreview } from "components/preview";
import {
  useGetIntegrationUserFolderQuery,
  type IntegrationUserFolderResponse,
} from "features/integrations/integrations";
import {
  useGetFileSystemQuery,
  useLazyGetFolderSyncStatusQuery,
} from "features/fileSystem/filesStorage";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useViewAs } from "hooks/useViewAs";
import { mayDownloadTopFolder } from "utils/roles";
import { useDownloadManager } from "hooks/useDownloadManager";
import {
  isTopDocumentFolderDownloadTarget,
} from "utils/topFolderDownload";
import { folderMappingsStorage, paginationStorage } from "utils/localStorage";
import { storagePaginationValues } from "utils/constants";
import type { FileItem } from "types/interfaces/filesStorage";
import type { FolderType } from "types/interfaces/filesStorage";

function normPath(s: string): string {
  return (s || "").replace(/\/+$/, "");
}

interface IntegrationFilesPanelProps {
  /** Legacy: resolve the folder from a ConnectionsMatch. */
  connectionMatchId?: number;
  /**
   * Pre-resolved folder metadata (integration-platform beta path). When given,
   * the legacy connection-match folder lookup is skipped.
   */
  folderMetaOverride?: IntegrationUserFolderResponse;
  integrationLabel?: string;
  canUseFileDateFeatures: boolean;
}

export default function IntegrationFilesPanel({
  connectionMatchId,
  folderMetaOverride,
  integrationLabel = "Integration folder",
  canUseFileDateFeatures,
}: IntegrationFilesPanelProps) {
  // Seed from the pre-resolved folder when available: the very first render
  // must never query with an empty prefix - GET /web/folders/?prefix= is the
  // ROOT Data Storage listing (admins see every client's folders there).
  const [currentPrefix, setCurrentPrefix] = useState(() =>
    normPath(folderMetaOverride?.user_folder?.folder_prefix ?? ""),
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [currentContinuationToken, setCurrentContinuationToken] = useState<
    string | null
  >(null);
  const [pageTokens, setPageTokens] = useState<string[]>([]);
  const [currentSearch, setCurrentSearch] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState("name_asc");
  const [currentLimit, setCurrentLimit] = useState(() => {
    const saved = paginationStorage.getPaginationValue();
    return storagePaginationValues.includes(saved) ? saved : 25;
  });
  const dispatch = useAppDispatch();
  const { profile: viewedProfile } = useViewAs();

  const queriedFolder = useGetIntegrationUserFolderQuery(
    connectionMatchId ?? 0,
    {
      skip: folderMetaOverride != null || connectionMatchId == null,
    },
  );
  const folderMeta = folderMetaOverride ?? queriedFolder.data;
  const folderLoading = folderMetaOverride ? false : queriedFolder.isLoading;
  const folderError = folderMetaOverride ? false : queriedFolder.isError;
  const folderErrorDetail = queriedFolder.error;

  const rootPrefix = normPath(folderMeta?.user_folder?.folder_prefix ?? "");

  useEffect(() => {
    setCurrentPage(1);
    setPageTokens([]);
    setCurrentContinuationToken(null);
    setCurrentSearch(null);
    setSortBy("name_asc");
    setCurrentPrefix(normPath(folderMeta?.user_folder?.folder_prefix ?? ""));
  }, [connectionMatchId, folderMeta?.user_folder?.folder_prefix]);

  const sortField = sortBy.split("_")[0] as "name" | "date" | "size";
  const sortOrder = sortBy.split("_")[1] as "asc" | "desc";
  const apiSortField =
    !canUseFileDateFeatures && sortField === "date" ? "name" : sortField;
  const apiSortOrder =
    !canUseFileDateFeatures && sortField === "date" ? "asc" : sortOrder;

  // The listing endpoint is scoped ONLY by the prefix string, so an empty or
  // out-of-root prefix (a not-yet-seeded state on the legacy path) must never
  // reach it - it would return other clients' folders.
  const prefixIsUnderRoot =
    !!rootPrefix &&
    (normPath(currentPrefix) === rootPrefix ||
      normPath(currentPrefix).startsWith(`${rootPrefix}/`));
  const skipFileSystem =
    !folderMeta?.success ||
    folderMeta.user_folder == null ||
    !rootPrefix ||
    !prefixIsUnderRoot ||
    folderLoading;

  const fileSystemQueryArgs = useMemo(
    () => ({
      prefix: currentPrefix,
      continuation_token: currentContinuationToken,
      search: currentSearch,
      limit: currentLimit,
      folder_type: "all" as FolderType,
      page: currentPage,
      sort_field: apiSortField,
      sort_order: apiSortOrder,
    }),
    [
      currentPrefix,
      currentContinuationToken,
      currentSearch,
      currentLimit,
      currentPage,
      apiSortField,
      apiSortOrder,
    ],
  );

  const {
    data: fileSystemData,
    isLoading: fileSystemLoading,
    isFetching: fileSystemFetching,
    error: fileSystemError,
    refetch: refetchFileSystem,
  } = useGetFileSystemQuery(fileSystemQueryArgs, {
    skip: skipFileSystem,
    // Refetch on every mount (tab switches remount this panel) so we never
    // serve a stale cached listing - files keep arriving between visits.
    refetchOnMountOrArgChange: true,
  });

  const isDbBacked = !!fileSystemData?.total_pages;

  const blockTopFolderZipWhileFolderSyncing =
    fileSystemData?.sync_status === "syncing";
  const downloadManager = useDownloadManager({
    blockTopFolderZipWhileFolderSyncing,
  });
  const { openPreview } = useFilePreview();

  useEffect(() => {
    if (fileSystemData?.sync_status !== "syncing") return;
    const t = setInterval(() => void refetchFileSystem(), 3000);
    return () => clearInterval(t);
  }, [fileSystemData?.sync_status, refetchFileSystem]);

  // Non-blocking "new data arrived" detector (same pattern as HomePage):
  // integration runs keep archiving files while the tab is open - poll
  // data_updated_at and refetch when it advances.
  const [triggerFolderSyncStatus] = useLazyGetFolderSyncStatusQuery();
  const lastSeenDataUpdatedAtRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    lastSeenDataUpdatedAtRef.current = fileSystemData?.data_updated_at ?? null;
  }, [fileSystemData?.data_updated_at, fileSystemData?.user_folder_id]);

  const currentFolderId = fileSystemData?.user_folder_id ?? null;
  useEffect(() => {
    if (!currentFolderId) return;
    if (fileSystemData?.sync_status === "syncing") return; // full sync owns the UI
    const interval = setInterval(async () => {
      try {
        const res = await triggerFolderSyncStatus(currentFolderId).unwrap();
        if (res.sync_status === "syncing") return;
        const incoming = res.data_updated_at ?? null;
        const baseline = lastSeenDataUpdatedAtRef.current ?? null;
        const incomingMs = incoming ? Date.parse(incoming) : NaN;
        const baselineMs = baseline ? Date.parse(baseline) : NaN;
        if (
          !Number.isNaN(incomingMs) &&
          (Number.isNaN(baselineMs) || incomingMs > baselineMs)
        ) {
          lastSeenDataUpdatedAtRef.current = incoming;
          void refetchFileSystem();
        }
      } catch {
        // ignore transient heartbeat errors
      }
    }, 12000);
    return () => clearInterval(interval);
  }, [
    currentFolderId,
    fileSystemData?.sync_status,
    triggerFolderSyncStatus,
    refetchFileSystem,
  ]);

  useEffect(() => {
    if (isDbBacked) return;
    if (fileSystemData?.continuation_token && currentPage > 0) {
      setPageTokens((prev) => {
        const next = [...prev];
        if (currentPage > next.length) {
          next.push(fileSystemData.continuation_token!);
        } else {
          next[currentPage - 1] = fileSystemData.continuation_token!;
        }
        return next;
      });
    } else if (!fileSystemData?.continuation_token && currentPage > 0) {
      setPageTokens((prev) => prev.slice(0, currentPage - 1));
    }
  }, [fileSystemData?.continuation_token, currentPage, isDbBacked]);

  useEffect(() => {
    const effectiveSort = sortBy;
    const sortRequiresFiles =
      effectiveSort === "date_desc" ||
      effectiveSort === "date_asc" ||
      effectiveSort === "size_desc" ||
      effectiveSort === "size_asc";
    if (
      isDbBacked &&
      fileSystemData?.has_direct_files === false &&
      sortRequiresFiles
    ) {
      setSortBy("name_asc");
      setCurrentPage(1);
      setPageTokens([]);
      setCurrentContinuationToken(null);
    }
  }, [isDbBacked, fileSystemData?.has_direct_files, sortBy]);

  const isUnderRoot = useCallback(
    (path: string) => {
      const p = normPath(path);
      const r = rootPrefix;
      if (!r) return false;
      return p === r || p.startsWith(`${r}/`);
    },
    [rootPrefix],
  );

  const handleItemClick = (item: FileItem) => {
    if (item.ftype !== "folder") return;
    if (!isUnderRoot(item.fsource)) return;
    const original = fileSystemData?.folders.find(
      (f) => f.fsource === item.fsource,
    );
    const fnameToStore = original?.fname || item.fname;
    folderMappingsStorage.clearIrrelevantMappings(item.fsource);
    folderMappingsStorage.setFolderMapping(fnameToStore, item.fsource);
    setCurrentPrefix(item.fsource);
    setCurrentContinuationToken(null);
    setCurrentPage(1);
    setPageTokens([]);
  };

  const handlePreviewClick = (item: FileItem) => {
    const original =
      fileSystemData?.folders.find((f) => f.fsource === item.fsource) || item;
    openPreview(original);
  };

  const handleBack = () => {
    if (!rootPrefix) return;
    const n = normPath(currentPrefix);
    if (n === rootPrefix) return;
    const rel = n.slice(rootPrefix.length).replace(/^\//, "");
    const parts = rel.split("/").filter(Boolean);
    parts.pop();
    const parent =
      parts.length > 0 ? `${rootPrefix}/${parts.join("/")}` : rootPrefix;
    folderMappingsStorage.clearMappingsForHigherLevel(parent);
    setCurrentPrefix(parent);
    setCurrentContinuationToken(null);
    setCurrentPage(1);
    setPageTokens([]);
  };

  const handleBreadcrumbClick = (targetPrefix: string) => {
    const t = normPath(targetPrefix);
    if (!isUnderRoot(t) && t !== "") return;
    folderMappingsStorage.clearMappingsForHigherLevel(t);
    setCurrentPrefix(t || rootPrefix);
    setCurrentContinuationToken(null);
    setCurrentPage(1);
    setPageTokens([]);
  };

  const buildBreadcrumbs = () => {
    const crumbs: { label: string; path: string; clickable: boolean }[] = [
      {
        label: integrationLabel,
        path: rootPrefix,
        clickable: normPath(currentPrefix) !== rootPrefix,
      },
    ];
    if (!currentPrefix || normPath(currentPrefix) === rootPrefix) {
      return crumbs;
    }
    const rel = normPath(currentPrefix)
      .slice(rootPrefix.length)
      .replace(/^\//, "");
    const parts = rel.split("/").filter(Boolean);
    let acc = rootPrefix;
    for (let i = 0; i < parts.length; i++) {
      acc = `${acc}/${parts[i]}`;
      const isLast = i === parts.length - 1;
      const mapped = folderMappingsStorage.getFname(acc);
      crumbs.push({
        label: mapped || parts[i],
        path: acc,
        clickable: !isLast,
      });
    }
    return crumbs;
  };

  const handleLimitChange = (limit: number) => {
    paginationStorage.setValidPaginationValue(limit, storagePaginationValues);
    setCurrentLimit(limit);
    setCurrentPage(1);
    setCurrentContinuationToken(null);
    setPageTokens([]);
  };

  const handleSortChange = (next: string) => {
    setSortBy(next);
    setCurrentPage(1);
    setCurrentContinuationToken(null);
    setPageTokens([]);
  };

  const handlePageChange = (page: number) => {
    if (isDbBacked) {
      if (page < 1 || page > (fileSystemData?.total_pages || 1)) return;
      setCurrentPage(page);
      return;
    }
    const maxPages = fileSystemData?.continuation_token
      ? pageTokens.length + 1
      : currentPage;
    if (page < 1 || page > maxPages) return;
    if (page === 1) {
      setCurrentContinuationToken(null);
    } else if (page <= pageTokens.length + 1) {
      setCurrentContinuationToken(pageTokens[page - 2]);
    } else {
      return;
    }
    setCurrentPage(page);
  };

  const handleSearch = (term: string | null) => {
    setCurrentSearch(term);
    setCurrentPage(1);
    setCurrentContinuationToken(null);
    setPageTokens([]);
  };

  const handleDownloadClick = async (item: FileItem) => {
    // Same scope guard as navigation: rows outside the bound folder (e.g.
    // from a cached root listing) must not be downloadable from here.
    if (!isUnderRoot(item.fsource)) return;
    const original =
      fileSystemData?.folders.find((f) => f.fsource === item.fsource) || item;

    const isTop =
      original.ftype === "folder" &&
      isTopDocumentFolderDownloadTarget(original, rootPrefix);

    if (isTop) {
      if (!mayDownloadTopFolder(viewedProfile)) {
        dispatch(
          pushNotification({
            message: "You do not have permission to download this folder.",
            severity: "error",
          }),
        );
        return;
      }
      const r = await downloadManager.downloadTopFolder(original);
      if (!r.success && r.message) {
        dispatch(
          pushNotification({
            message: r.message,
            severity: r.notificationSeverity === "info" ? "info" : "error",
          }),
        );
      }
      return;
    }

    const result = await downloadManager.downloadItem(original);
    if (!result.success) {
      dispatch(
        pushNotification({ message: result.message, severity: "error" }),
      );
      return;
    }
    const lower = result.message.toLowerCase();
    if (!lower.includes("building") && !lower.includes("generating")) {
      dispatch(
        pushNotification({ message: result.message, severity: "success" }),
      );
    }
  };

  const sortSelectValue = sortBy;

  const mayDownloadTopDocumentFolder = useMemo(
    () => mayDownloadTopFolder(viewedProfile),
    [viewedProfile],
  );

  const displayedFolders = useMemo(() => {
    // Defense in depth: even if a cached/raced response carries rows from
    // outside the bound folder (root listing), never render them here.
    const rows = (fileSystemData?.folders ?? []).filter((row) =>
      isUnderRoot(row.fsource),
    );
    if (apiSortField !== "name") return rows;
    const copy = [...rows];
    copy.sort((a, b) => {
      const cmp = a.fname.localeCompare(b.fname, undefined, {
        sensitivity: "base",
        numeric: true,
      });
      return apiSortOrder === "desc" ? -cmp : cmp;
    });
    return copy;
  }, [fileSystemData?.folders, apiSortField, apiSortOrder, isUnderRoot]);

  if (folderLoading) {
    return (
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          border: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          py: 6,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (folderError) {
    const msg =
      (folderErrorDetail as { data?: { error?: string } })?.data?.error ||
      "Failed to load folder link";
    return (
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          border: "1px solid",
          borderColor: "divider",
          p: 2,
        }}
      >
        <Alert severity="error">{msg}</Alert>
      </Box>
    );
  }

  if (!folderMeta?.user_folder) {
    return (
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          border: "1px solid",
          borderColor: "divider",
          p: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {folderMeta?.message ||
            "No Data Storage folder is linked to this integration yet. Connect a folder from Data Storage (super admin) to browse files here."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          mb: 1.5,
          flexShrink: 0,
          px: { xs: 1.5, sm: 2 },
          py: { xs: 1, sm: 1 },
          backgroundColor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          border: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 2,
        }}
      >
        {normPath(currentPrefix) !== rootPrefix && (
          <IconButton
            onClick={handleBack}
            aria-label="Back"
            size="small"
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              backgroundColor: "background.paper",
              color: "primary.main",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
        )}
        <Breadcrumbs
          aria-label="breadcrumb"
          separator="/"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            "& .MuiBreadcrumbs-ol": {
              flexWrap: "nowrap",
              overflow: "hidden",
            },
            "& .MuiBreadcrumbs-li": {
              maxWidth: "100%",
              overflow: "hidden",
              display: "flex",
            },
          }}
        >
          {buildBreadcrumbs().map((c, i) =>
            c.clickable ? (
              <Tooltip key={`${c.path}-${i}`} title={c.label}>
                <Link
                  component="button"
                  variant="body2"
                  underline="hover"
                  onClick={() => handleBreadcrumbClick(c.path)}
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    border: "none",
                    background: "none",
                    p: 0,
                    font: "inherit",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textAlign: "left",
                  }}
                >
                  {i === 0 && <Home fontSize="small" sx={{ flexShrink: 0 }} />}
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                  >
                    {c.label}
                  </Box>
                </Link>
              </Tooltip>
            ) : (
              <Tooltip key={`${c.path}-${i}`} title={c.label}>
                <Typography
                  variant="body2"
                  color="text.primary"
                  fontWeight={600}
                  component="span"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}
                >
                  {i === 0 && <Home fontSize="small" sx={{ flexShrink: 0 }} />}
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      minWidth: 0,
                    }}
                  >
                    {c.label}
                  </Box>
                </Typography>
              </Tooltip>
            ),
          )}
        </Breadcrumbs>

        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            gap: 1,
            height: 40,
            flexBasis: { xs: "100%", sm: "auto" },
            width: { xs: "100%", sm: "auto" },
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              maxWidth: { xs: "none", sm: 400 },
            }}
          >
            <FileSearch
              key={connectionMatchId ?? rootPrefix}
              onSearch={handleSearch}
              placeholder="Search files and folders..."
              initialValue={currentSearch ?? ""}
            />
          </Box>
          <FormControl
            size="small"
            sx={{
              minWidth: { xs: 140, sm: 170 },
              height: "100%",
              flexShrink: 0,
              "& .MuiInputBase-root": { height: "100%" },
            }}
          >
            <Select<string>
              value={sortSelectValue}
              onChange={(e: SelectChangeEvent<string>) =>
                handleSortChange(String(e.target.value))
              }
              sx={{ fontSize: "0.85rem", height: "100%" }}
            >
              {canUseFileDateFeatures
                ? [
                    <MenuItem
                      key="date_desc"
                      value="date_desc"
                      disabled={!fileSystemData?.has_direct_files}
                    >
                      Date (Newest first)
                    </MenuItem>,
                    <MenuItem
                      key="date_asc"
                      value="date_asc"
                      disabled={!fileSystemData?.has_direct_files}
                    >
                      Date (Oldest first)
                    </MenuItem>,
                  ]
                : null}
              <MenuItem
                value="size_desc"
                disabled={!fileSystemData?.has_direct_files}
              >
                Size (Largest first)
              </MenuItem>
              <MenuItem
                value="size_asc"
                disabled={!fileSystemData?.has_direct_files}
              >
                Size (Smallest first)
              </MenuItem>
              <MenuItem value="name_asc">Name (A → Z)</MenuItem>
              <MenuItem value="name_desc">Name (Z → A)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box
        sx={{
          flex: "1 1 0",
          minHeight: 0,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.paper",
          borderRadius: 1,
          boxShadow: 1,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: "1 1 0",
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <FileList
            showFileDate={canUseFileDateFeatures}
            timeDisplayMode="local"
            hideEmptyLabel={normPath(currentPrefix) === rootPrefix}
            items={displayedFolders}
            loading={fileSystemLoading || fileSystemFetching}
            error={fileSystemError ? "Failed to load files" : null}
            currentLimit={currentLimit}
            onLimitChange={handleLimitChange}
            onItemClick={handleItemClick}
            onDownloadClick={handleDownloadClick}
            topDocumentFolderRootPrefix={rootPrefix || null}
            mayDownloadTopDocumentFolder={mayDownloadTopDocumentFolder}
            disableTopFolderZipDuringSync={
              fileSystemData?.sync_status === "syncing"
            }
            onPreviewClick={handlePreviewClick}
            showDownloadButton
            showPreviewButton
            disableFileClick
            showMoreOptions={false}
            showPagination
            size="medium"
            variant="default"
            maxHeight="100%"
            currentPage={currentPage}
            totalCount={fileSystemData?.total_count}
            totalPages={
              isDbBacked
                ? (fileSystemData?.total_pages ?? 1)
                : fileSystemData?.continuation_token
                  ? pageTokens.length + 1
                  : currentPage
            }
            onPageChange={handlePageChange}
            pageTokens={pageTokens}
            downloadingItems={downloadManager.downloadingItems}
            downloadProgress={downloadManager.downloadProgress}
            hasNextPage={
              isDbBacked
                ? currentPage < (fileSystemData?.total_pages || 1)
                : !!fileSystemData?.continuation_token
            }
          />
        </Box>
      </Box>

      {fileSystemData?.sync_status === "syncing" && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, flexShrink: 0 }}
        >
          Folder sync in progress…
        </Typography>
      )}
    </Box>
  );
}
